import { ButtonLoading } from "@/components/button-loading";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@radix-ui/react-icons";
import { ListingDTO } from "@shaarp/common";
import { FormEvent, useEffect, useState } from "react";

export default function DialogAssignListing({
  listing,
  onClose,
  onAssigned,
}: {
  listing: ListingDTO;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { users, apiFetchUsers } = useUsers();
  const { teamUser } = useUser();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    listing.assignees?.map((a) => a.id) ?? [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    apiFetchUsers();
  }, []);

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const initialIds = listing.assignees?.map((a) => a.id) ?? [];
    const toAdd = selectedUserIds.filter((id) => !initialIds.includes(id));
    const toRemove = initialIds.filter((id) => !selectedUserIds.includes(id));

    try {
      if (toAdd.length > 0) {
        await api.post(`/listing/${listing.id}/assignees`, {
          userIds: toAdd,
        });
      }
      await Promise.all(
        toRemove.map((userId) =>
          api.delete(`/listing/${listing.id}/assignees/${userId}`),
        ),
      );
      toast({
        variant: "default",
        title: t("listing.dialog.assign.success.title"),
      });
      onAssigned();
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const assignableUsers = users.filter(
    (u) => u.id !== teamUser?.id && u.id !== listing.author?.id,
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] w-full">
        <DialogHeader>
          <DialogTitle>{t("listing.dialog.assign.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <Separator className="my-4" />
          <div className="overflow-auto max-h-[60vh]">
            <Command>
              <CommandInput placeholder={t("common.search")} />
              <CommandList>
                <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                <CommandGroup>
                  {assignableUsers.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.fullName}
                      onSelect={() => toggleUser(user.id)}
                      className="flex gap-2 items-center cursor-pointer"
                    >
                      <CheckIcon
                        className={cn(
                          "w-4 h-4 text-primary opacity-0",
                          selectedUserIds.includes(user.id) && "opacity-100",
                        )}
                      />
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={user.imageUrl} />
                        <AvatarFallback>
                          {user.fullName?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span>{user.fullName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
          <Separator className="my-4" />
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                {t("listing.dialog.assign.buttons.close")}
              </Button>
            </DialogClose>
            <ButtonLoading type="submit" isLoading={isLoading}>
              {t("listing.dialog.assign.buttons.save")}
            </ButtonLoading>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
